/* eslint-disable no-bitwise */
export const ipv4Regex = /^(\d{1,3}\.){3,3}\d{1,3}$/;
export const ipv6Regex =
  /^(::)?(((\d{1,3}\.){3}(\d{1,3}){1})?([0-9a-f]){0,4}:{0,2}){1,8}(::)?$/i;

export const isV4Format = (ip: string) => ipv4Regex.test(ip);
export const isV6Format = (ip: string) => ipv6Regex.test(ip);
export const isSubnetMaskLength = (subnetMask: string) =>
  parseInt(subnetMask, 10) > 0 && parseInt(subnetMask, 10) <= 32;
export const isGateway = (gateway: string) => ipv4Regex.test(gateway);

/**
 * Convert IP address from string to 32-bit integer
 * @param {string} ipAddress - IP address (e.g. "192.168.1.123")
 * @returns {number} - IP address as an integer
 */
function ipToInteger(ipAddress: string): number {
  return ipAddress
    .split('.')
    .reduce((acc, octet) => (acc << 8) + parseInt(octet, 10), 0);
}

/**
 * Convert 32-bit integer to IP address as a string
 * @param {number} ipInt - IP address as an integer
 * @returns  {string} - IP address (e.g. "192.168.1.0")
 */
function integerToIp(ipInt: number): string {
  return [
    ipInt >>> 24,
    (ipInt >>> 16) & 255,
    (ipInt >>> 8) & 255,
    ipInt & 255,
  ].join('.');
}

/**
 * Determine the network address from IP address and subnet mask
 * @param {string} ipAddress - IP address (e.g. "192.168.1.123")
 * @param {number} subnetMaskLength - Subnet mask length (e.g. 24)
 * @returns {string} - Network address (e.g. "192.168.1.0")
 */
export function calculateNetworkAddress(
  ipAddress: string,
  subnetMaskLength: number,
): string {
  const ipInt = ipToInteger(ipAddress);
  const mask = -1 << (32 - subnetMaskLength); // Create a mask for the subnet
  const networkAddressInt = ipInt & mask; // Calculate the network address
  return integerToIp(networkAddressInt);
}

/**
 * Determine the network address from IP address and subnet mask
 * @param {string} ipAddress - IP address (e.g. "192.168.1.123")
 * @param {string} subnetMask - Subnet mask (e.g. "255.255.255.0")
 * @returns {string} - Network address (e.g. "192.168.1.0")
 */
export function calculateNetworkAddress2(
  ipAddress: string,
  subnetMask: string,
): string {
  if (!ipAddress || !subnetMask) return '';
  const ipInt = ipToInteger(ipAddress);
  const maskInt = ipToInteger(subnetMask);
  const networkAddressInt = ipInt & maskInt;
  return integerToIp(networkAddressInt);
}

/**
 * Check if two subnets are valid (do not overlap)
 * @param {string} subnetA - Subnet A (e.g. "192.168.1.0/24")
 * @param {string} subnetB - Subnet B (e.g. "192.168.2.0/24")
 * @returns {boolean} - true if they do not overlap, false if they overlap
 */
export function validateSubnets(subnetA: string, subnetB: string) {
  const [ipA, maskLengthA] = subnetA.split('/');
  const [ipB, maskLengthB] = subnetB.split('/');

  const networkA = ipToInteger(
    calculateNetworkAddress(ipA, parseInt(maskLengthA, 10)).split('/')[0],
  );
  const networkB = ipToInteger(
    calculateNetworkAddress(ipB, parseInt(maskLengthB, 10)).split('/')[0],
  );

  const maskA = -1 << (32 - parseInt(maskLengthA, 10));
  const maskB = -1 << (32 - parseInt(maskLengthB, 10));

  // Check if two subnets overlap
  return !(
    (networkA & maskB) === (networkB & maskB) ||
    (networkB & maskA) === (networkA & maskA)
  );
}

/**
 * Check the validity of IP in subnet
 * @param {string} ip - User-entered IP address (e.g. "192.168.1.10")
 * @param {string} gateway - Gateway address (e.g. "192.168.1.0/24")
 * @param {string} subnet - Main subnet (e.g. "192.168.1.0/24")
 * @returns {boolean} - true if valid, false if invalid
 */

export function validateIpInSubnet(
  ip: string,
  gateway: string,
  subnet: string,
) {
  const [subnetIp, subnetMaskLengthStr] = subnet.split('/');
  const subnetMaskLength = parseInt(subnetMaskLengthStr, 10);

  const networkAddress = calculateNetworkAddress(subnetIp, subnetMaskLength);
  const networkAddressIp = ipToInteger(networkAddress);
  const subnetMask = -1 << (32 - subnetMaskLength);

  const ipInt = ipToInteger(ip);
  const gatewayInt = ipToInteger(gateway);

  // Check if IP and gateway are in the same subnet
  const isIpInSubnet = (ipInt & subnetMask) === networkAddressIp;
  const isGatewayInSubnet = (gatewayInt & subnetMask) === networkAddressIp;

  // Check if IP and gateway are not network address or broadcast address
  const isIpValid =
    ipInt !== networkAddressIp && ipInt !== (networkAddressIp | ~subnetMask);
  const isGatewayValid =
    gatewayInt !== networkAddressIp &&
    gatewayInt !== (networkAddressIp | ~subnetMask);

  // Check if IP and gateway are in the same subnet and are not network address or broadcast address
  return isIpInSubnet && isGatewayInSubnet && isIpValid && isGatewayValid;
}

/**
 * Convert subnet mask length to subnet mask
 * @param {number} subnetMaskLength - Subnet mask length (e.g. 24)
 * @returns {string} - Subnet mask (e.g. "255.255.255.0")
 */
export function convertSubnetMaskLengthToMask(
  subnetMaskLength: number,
): string {
  // Handle edge cases to avoid bitwise shift wrapping
  if (subnetMaskLength === 0) {
    return '0.0.0.0';
  }
  if (subnetMaskLength === 32) {
    return '255.255.255.255';
  }
  const mask = -1 << (32 - subnetMaskLength);
  return integerToIp(mask);
}

/**
 * Convert subnet mask to subnet mask length
 * @param {string} subnetMask - Subnet mask (e.g. "255.255.255.0")
 * @returns {number} - Subnet mask length (e.g. 24), returns 0 if mask is non-contiguous (invalid)
 */
export function convertSubnetMaskToLength(subnetMask: string): number {
  // Check if mask is contiguous by converting to binary and checking for '01' pattern
  const binaryMask = subnetMask
    .split('.')
    .map((octet) => parseInt(octet, 10).toString(2).padStart(8, '0'))
    .join('');

  // Non-contiguous masks have '01' pattern (0 after 1)
  if (binaryMask.includes('01')) {
    return 0;
  }

  let maskInt = ipToInteger(subnetMask);
  let maskLength = 0;
  while (maskInt & (1 << 31)) {
    maskLength += 1;
    maskInt <<= 1;
  }
  return maskLength;
}

/**
 * Increment IP address by 1
 * @param {string} ip - IP address (e.g. "192.168.1.1")
 * @param {number} step - Number of steps to increment by (default is 1)
 * @param {object} options - Optional validation options
 * @param {string} options.subnet - Subnet address (e.g. "192.168.1.0")
 * @param {string} options.subnetMask - Subnet mask (e.g. "255.255.255.0")
 * @returns {string | undefined} - Incremented IP address (e.g. "192.168.1.2") or undefined if outside subnet
 */
export function incrementIp(
  ip: string,
  step: number = 1,
  options?: { subnet?: string; subnetMask?: string },
): string | undefined {
  if (!ip) return undefined;
  const ipInt = ipToInteger(ip) + step;
  const nextIp = integerToIp(ipInt);

  // If subnet validation is provided, check if the next IP is within bounds
  if (options?.subnet && options?.subnetMask) {
    // Check if the next IP is a valid host address in the subnet
    if (!isValidHostAddress(nextIp, options.subnet, options.subnetMask)) {
      return undefined;
    }
  }

  return nextIp;
}

export function isValidSubnetMask(mask: string): boolean {
  if (!mask) return false;
  const subnetRegex =
    /^(((0|128|192|224|240|248|252|254)\.0\.0\.0)|(255\.(0|128|192|224|240|248|252|254)\.0\.0)|(255\.255\.(0|128|192|224|240|248|252|254)\.0)|(255\.255\.255\.(0|128|192|224|240|248|252|254)))/gm;
  return subnetRegex.test(mask);
}

function ipToBinary(ip: string): string {
  return ip
    .split('.')
    .map((octet: string) =>
      `00000000${parseInt(octet, 10).toString(2)}`.slice(-8),
    )
    .join('.');
}

export function isValidSubnetAddress(ip: string, mask: string): boolean {
  const ipBinary = ipToBinary(ip).replace(/\./g, '');
  const maskBinary = ipToBinary(mask).replace(/\./g, '');
  const maskedIpBinary = ipBinary
    .slice(0, maskBinary.indexOf('0'))
    .padEnd(32, '0');
  return maskedIpBinary === ipBinary;
}

export function isWhitelistedSubnet(ip: string): boolean {
  const whitelistedRanges = [
    [/^192\.168\.(\d{1,3})\.(\d{1,3})$/, [16, 31]],
    [/^172\.(1[6-9]|2[0-9]|3[0-1])\.(\d{1,3})\.(\d{1,3})$/, [12, 27]],
    [/^10\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/, [8, 23]],
  ] as const;
  return whitelistedRanges.some(([regex]) => regex.test(ip));
}

export function isValidIPAddress(ip = ''): boolean {
  const ipRegex = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/;
  const match = ip.match(ipRegex);
  if (!match) return false;
  return match
    .slice(1, 5)
    .every((octet) => parseInt(octet, 10) >= 0 && parseInt(octet, 10) <= 255);
}

export function isUniqueSubnet(
  existingSubnets: { subnet: string; mask: string }[],
  newSubnet: string,
  checkMask: string,
): boolean {
  const newMasked = ipToBinary(newSubnet).slice(
    0,
    ipToBinary(checkMask).indexOf('0'),
  );
  return !existingSubnets.some(
    ({ subnet, mask }: { subnet: string; mask: string }) =>
      ipToBinary(subnet).slice(0, ipToBinary(mask).indexOf('0')) === newMasked,
  );
}

export function isValidHostAddress(ip: string, subnet: string, mask: string) {
  if (!ip || !subnet || !mask) return false;
  const subnetBinary = ipToBinary(subnet).replace(/\./g, '');
  const maskBinary = ipToBinary(mask).replace(/\./g, '');
  const ipBinary = ipToBinary(ip).replace(/\./g, '');

  // Check if IP is in subnet
  const isInSubnet = ipBinary.startsWith(
    subnetBinary.slice(0, maskBinary.indexOf('0')),
  );

  // Check if IP is network address or broadcast address
  const networkAddress = calculateNetworkAddress2(ip, mask);
  const broadcastAddress = incrementIp(
    networkAddress,
    2 ** (32 - convertSubnetMaskToLength(mask)) - 1,
  );

  return isInSubnet && ip !== networkAddress && ip !== broadcastAddress;
}

// Check how many leading octets are 255
export function countLeading255Octets(subnetMask: string): number {
  const octets = subnetMask.split('.');
  for (let i = 0; i < octets.length; i += 1) {
    if (octets[i] !== '255') {
      return i;
    }
  }
  return octets.length;
}
